<template>
  <app-dialog
    :title="title"
    v-bind="$attrs"
    v-model="open"
    :closable="!reset"
  >
    <template v-if="reset && step != 2">
      <div v-if="step == 0">
        Let's set up a custom pin so we can protect your information.
      </div>
      <div v-if="step == 1">
        Please confirm your new pin.
      </div>
    </template>
    <div v-else-if="step != 2">
      <template v-if="user.id == $auth.user!.id">
        Since multiple users will share the same account, please set a pin so that you can protect your information.
      </template>
      <template v-else>
        Since multiple users will share the same account, please set a pin for {{ user.name }} so that they can protect their information.
      </template>
    </div>
    <div v-else>
      <template v-if="user.id == $auth.user!.id">
        Thanks! We’ve recorded your pin. In the future to sign on select your profile and enter your pin.
      </template>
      <template v-else>
        Thanks! We’ve recorded your pin. In the future, when {{ user.name }} wants to sign on all they have to do is select their profile and enter their pin.
      </template>
    </div>

    <div
      class="text-center mb-n4"
      v-if="!$auth.roleAtLeast(roles.AUTONOMOUS) && step != 2"
    >
      <v-checkbox
        v-model="noPinRequired"
        class="d-inline-block"
        hide-details
        label="Opt out of using a pin to protect your profile."
      />
    </div>

    <template v-if="!noPinRequired">
      <div
        class="text-red text-center mt-4"
        v-if="error"
        v-text="error"
      />

      <v-otp-input
        v-if="step === 0"
        v-model="pin"
        :error="!!error"
        length="4"
        width="250"
        class="pb-0"
        base-color="primary"
        @keydown.enter="next"
        @change="error = ''"
      />
      <v-otp-input
        v-else-if="step === 1"
        v-model="confirmPin"
        :error="!!error"
        length="4"
        width="250"
        class="pb-0"
        base-color="primary"
        @keydown.enter="next"
        @change="error = ''"
      />

      <div class="d-flex ga-4 mt-4 justify-center">
        <v-btn-secondary
          v-if="showBack"
          class="flex-grow-1"
          @click="prev"
          :disabled="loading === 'continue'"
        >
          Back
        </v-btn-secondary>
        <v-btn-primary
          :class="{'w-50': !showBack, 'flex-grow-1': showBack}"
          @click="next"
          :disabled="step === 0 && pin.length !== 4 || step === 1 && confirmPin.length !== 4"
          :loading="loading === 'continue'"
        >
          Continue
        </v-btn-primary>
      </div>
    </template>
    <div
      v-else
      class="d-flex ga-4 mt-4 justify-center"
    >
      <v-btn-primary
        class="w-50"
        @click="savePin"
        :loading="loading === 'continue'"
      >
        Continue
      </v-btn-primary>
    </div>
  </app-dialog>
</template>
<script lang="ts">
import AppDialog from "@/components/AppDialog.vue"
import type { PropType } from 'vue'
import {roles} from "@/enums/roles"

export default {
  name: 'PinDialog',
  components: {AppDialog},
  inheritAttrs: false,
  emits: ['update:modelValue', 'success', 'add-member', 'close'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object as PropType<App.Models.User>,
      required: true
    },
    reset: Boolean,
  },
  data() {
    return {
      step: 0,
      pin: '',
      confirmPin: '',
      error: '',
      loading: '',
      noPinRequired: false,
      roles,
    }
  },
  computed: {
    open: {
      get() {
        return this.modelValue
      },
      set(value: boolean) {
        this.$emit('update:modelValue', value)
        if (!value) {
          this.$emit('close')
        }
      }
    },
    showBack() {
      return this.step > 0 && this.step < 2
    },
    title() {
      if (this.reset) this.step === 0 ? 'Reset Your Pin' : this.step === 1 ? 'Confirm Your Pin Reset' : 'Pin Reset'
      return this.step === 0 ? 'Add Pin' : this.step === 1 ? 'Confirm Pin' : 'Pin Added'
    }
  },
  methods: {
    async next() {
      if (this.step === 0 && this.pin.length !== 4 || this.step === 1 && this.confirmPin.length !== 4) {
        this.error = 'Pin must be 4 digits.'
        return
      }

      if (this.step === 1) {
        if (this.pin !== this.confirmPin) {
          this.error = 'Uh-oh. Pins don\'t match. Please try again.'
          return
        }
        const result = await this.savePin()
        if (!result) return
      }

      if (this.step === 2) {
        if (this.user.id === this.$auth.user!.id) {
          this.$emit('add-member')
        }

        this.$emit('success')
        this.open = false
        return
      }
      this.step++
    },
    prev() {
      if (this.step === 0) return
      this.step--
    },
    async savePin() {
      this.error = ''
      this.loading = 'continue'
      const { data: { message, errors }, status } = await this.$http.post('/users/' + this.user.id + '/set-pin', { pin: this.pin, noPin: this.noPinRequired }).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return false
      }

      if (this.user.id === this.$auth.user!.id) {
        this.$auth.loadUser(true)
      }

      if (this.noPinRequired) {
        this.$emit('success')
        this.open = false
        return true
      }

      return true
    }
  }
}
</script>
